// SchedulePage.jsx
import React, { useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Box,
  Chip,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/system";
import Header1 from "../Header/Header1";
import Footer2 from "../Footer/Footer2";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CodeIcon from "@mui/icons-material/Code";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { useAuth } from "../../AuthContext";
import axios from "axios";

/* -------------------- Styled Components -------------------- */

const DarkContainer = styled(Container)(({ theme }) => ({
  // backgroundColor: "#000",
   background: "radial-gradient(circle at top, #1e3c72, #000000)",
  color: "#fff",
  minHeight: "100vh",
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
}));

const GradientAccordion = styled(Accordion)(() => ({
  background: "#111",
  borderRadius: 14,
  boxShadow: "0 6px 24px rgba(0,0,0,0.6)",
  marginBottom: "16px",
  "&:before": { display: "none" },
}));

/* -------------------- Topic Chip -------------------- */

const TopicChip = ({ label }) => {

  useEffect(() => {
          window.scrollTo(0, 0);  // Scroll to top when this page is loaded
      }, []);

  let icon = <MenuBookIcon fontSize="small" />;
  let color = "info";

  const lower = label.toLowerCase();
  if (lower.includes("assignment")) {
    icon = <AssignmentIcon fontSize="small" />;
    color = "secondary";
  } else if (lower.includes("build") || lower.includes("hands")) {
    icon = <CodeIcon fontSize="small" />;
    color = "success";
  }

  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      color={color}
      sx={{ mr: 1, mb: 1 }}
    />
  );
};

/* -------------------- Session Card -------------------- */

const SessionCard = ({ session }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      mb: 3,
      borderRadius: 3,
      background: "linear-gradient(145deg, #121212, #1c1c1c)",
      border: "1px solid #2a2a2a",
      transition: "all 0.25s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
      },
    }}
  >
    <Box display="flex" alignItems="center" mb={1}>
      <Chip
        label={session.label}
        size="small"
        sx={{
          backgroundColor: "#0eaceb",
          color: "#000",
          fontWeight: "bold",
          mr: 1,
        }}
      />
      <Typography variant="h6" fontWeight="bold">
        {session.title}
      </Typography>
    </Box>

    <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
      {session.topics.map((topic, i) => (
        <TopicChip key={i} label={topic} />
      ))}
    </Box>
  </Paper>
);

/* -------------------- Schedule Data -------------------- */

const schedule = [
  {
    week: "Week 1",
    theme: "AI Foundations & Python",
    sessions: [
      {
        label: "Day 1 · Session 1",
        title: "Introduction & Tools Setup",
        topics: [
          "Program overview & expectations",
          "AI / ML / Deep Learning basics",
          "Python, Jupyter, core libraries setup",
        ],
      },
      {
        label: "Day 2 · Session 2",
        title: "Python for Data Science & Math Refresher",
        topics: [
          "NumPy & pandas essentials",
          "Linear algebra basics",
          "Assignment: data handling exercises",
        ],
      },
    ],
  },
  {
    week: "Week 2",
    theme: "Supervised Learning",
    sessions: [
      {
        label: "Day 3 · Session 3",
        title: "Regression & Classification",
        topics: [
          "Linear & logistic regression",
          "Loss functions & gradient descent",
        ],
      },
      {
        label: "Day 4 · Session 4",
        title: "Evaluation & Regularization",
        topics: [
          "Model metrics & validation",
          "Overfitting & regularization",
          "Assignment: ML models on real datasets",
        ],
      },
    ],
  },
  {
    week: "Week 3",
    theme: "Ensemble Learning",
    sessions: [
      {
        label: "Day 5 · Session 5",
        title: "Tree-Based Models",
        topics: [
          "Decision Trees & Random Forests",
          "Gradient boosting methods",
        ],
      },
      {
        label: "Day 6 · Session 6",
        title: "Feature Engineering & Tuning",
        topics: [
          "Feature selection & encoding",
          "Hyperparameter tuning",
          "Assignment: mini Kaggle challenge",
        ],
      },
    ],
  },
  {
    week: "Week 4",
    theme: "Deep Learning Foundations",
    sessions: [
      {
        label: "Day 7 · Session 7",
        title: "Neural Networks",
        topics: [
          "MLPs & activation functions",
          "Backpropagation & optimizers",
        ],
      },
      {
        label: "Day 8 · Session 8",
        title: "Deep Learning Hands-On",
        topics: [
          "PyTorch / TensorFlow basics",
          "Regularization in DL",
          "Assignment: neural network project",
        ],
      },
    ],
  },
  {
    week: "Week 5",
    theme: "Computer Vision",
    sessions: [
      {
        label: "Day 9 · Session 9",
        title: "CNNs & Transfer Learning",
        topics: [
          "CNN architectures",
          "Pretrained models",
        ],
      },
      {
        label: "Day 10 · Session 10",
        title: "Advanced CV Applications",
        topics: [
          "Object detection & segmentation",
          "Assignment: image classification task",
        ],
      },
    ],
  },
  {
    week: "Week 6",
    theme: "Natural Language Processing",
    sessions: [
      {
        label: "Day 11 · Session 11",
        title: "NLP Fundamentals",
        topics: [
          "Text preprocessing",
          "Word embeddings",
        ],
      },
      {
        label: "Day 12 · Session 12",
        title: "Transformers & LLMs",
        topics: [
          "Attention & transformers",
          "Prompt engineering",
          "Assignment: NLP mini project",
        ],
      },
    ],
  },
  {
    week: "Week 7",
    theme: "Advanced AI & Ethics",
    sessions: [
      {
        label: "Day 13 · Session 13",
        title: "Generative Models",
        topics: [
          "VAEs & GANs",
          "Diffusion models overview",
        ],
      },
      {
        label: "Day 14 · Session 14",
        title: "Reinforcement Learning & AI Ethics",
        topics: [
          "RL basics",
          "Fairness, bias & explainability",
          "Assignment: exploratory project",
        ],
      },
    ],
  },
  {
    week: "Week 8",
    theme: "Capstone Project",
    sessions: [
      {
        label: "Day 15 · Session 15",
        title: "Capstone Kickoff",
        topics: [
          "Project themes & team formation",
          "Architecture planning",
        ],
      },
      {
        label: "Day 16 · Session 16",
        title: "Implementation & Mentorship",
        topics: [
          "Project development",
          "Weekly check-ins & final presentation",
        ],
      },
    ],
  },
];

/* -------------------- Page Component -------------------- */

const API_URL = process.env.REACT_APP_API_URL;

const SchedulePage = () => {
  const { userId } = useAuth();

  useEffect(() => {
    if (!userId) return;
    axios.post(`${API_URL}/track-visit/`, { userId }).catch(() => {});
  }, [userId]);

  return (
    <>
      <Header1 />

      <DarkContainer maxWidth="md">
        <Typography
          variant="h1"
          align="center"
          sx={{
            fontWeight: 800,
            mb: 1,
            background: "linear-gradient(90deg, #00e5ff, #80d8ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          AI Immersion Program
        </Typography>

        <Typography align="center" sx={{ color: "#b0bec5", mb: 6 }}>
          Structured · Hands-On · Industry-Focused Learning Journey
        </Typography>

        {schedule.map((week) => (
          <GradientAccordion key={week.week}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#0eaceb" }} />}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {week.week}
                </Typography>
                <Typography variant="body2" sx={{ color: "#9ea7ad" }}>
                  {week.theme}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              {week.sessions.map((session, idx) => (
                <SessionCard key={idx} session={session} />
              ))}
            </AccordionDetails>
          </GradientAccordion>
        ))}
      </DarkContainer>

      <Footer2 />
    </>
  );
};

export default SchedulePage;
